import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Radar, Inbox, Folders, Users, BarChart3, Activity, Plug, BookOpen, LogOut, ChevronDown, RotateCcw, ShieldAlert, SlidersHorizontal, Route as RouteIcon, Sparkles, Menu } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PERSONA_LABEL } from "@/lib/labels";
import ThemeToggle from "@/components/ThemeToggle";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const navSections = [
  {
    label: "Workspace",
    items: [
      { to: "/", label: "Decision Radar", icon: Radar, testid: "nav-radar" },
      { to: "/signals", label: "Signals", icon: Inbox, testid: "nav-signals" },
      { to: "/cases", label: "Cases", icon: Folders, testid: "nav-cases" },
      { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
      { to: "/journeys", label: "Journeys", icon: RouteIcon, testid: "nav-journeys" },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/impact", label: "Business Impact", icon: BarChart3, testid: "nav-impact" },
      { to: "/observability", label: "Observability", icon: Activity, testid: "nav-observability" },
    ],
  },
  {
    label: "Setup",
    items: [
      { to: "/connectors", label: "Connectors", icon: Plug, testid: "nav-connectors" },
      { to: "/knowledge", label: "Knowledge", icon: BookOpen, testid: "nav-knowledge" },
      { to: "/settings", label: "Settings", icon: SlidersHorizontal, testid: "nav-settings",
        permission: "connector.manage" },
    ],
  },
];

export default function Layout() {
  const { user, persona, setPersona, logout, tenantMode, switchMode, pairedTenant,
          isPlatformAdmin, permissions } = useAuth();
  const [plan, setPlan] = useState(null);
  const [upgrade, setUpgrade] = useState(null);

  const { pathname } = useLocation();

  // Refetched on navigation and after each AI task, so the credit count in
  // the top bar does not go stale while someone works through cases.
  useEffect(() => {
    const load = () => api.get("/entitlements").then((r) => setPlan(r.data)).catch(() => {});
    load();
    const onUpgrade = (e) => { setUpgrade(e.detail); load(); };
    window.addEventListener("eos:upgrade-required", onUpgrade);
    window.addEventListener("eos:ai-used", load);
    return () => {
      window.removeEventListener("eos:upgrade-required", onUpgrade);
      window.removeEventListener("eos:ai-used", load);
    };
  }, [pathname]);
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [navOpen, setNavOpen] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [resetMsg, setResetMsg] = React.useState("");
  const [modeBusy, setModeBusy] = React.useState(false);

  if (!user) { window.location.href = "/login"; return null; }

  const modeColor = tenantMode === "live" ? "var(--risk-healthy)" : "var(--risk-waiting)";

  const onToggleMode = async () => {
    if (!pairedTenant) return;
    // Demo and live are separate tenants with separate data, so this is a
    // tenant switch, validated against membership on the server.
    const confirmed = window.confirm(
      `Switch to ${pairedTenant.name}?\n\n` +
      `This is a different tenant (${pairedTenant.environment}) with its own ` +
      `customers, signals and cases. Nothing crosses between them.`
    );
    if (!confirmed) return;
    setModeBusy(true);
    try {
      await switchMode();
    } catch (e) {
      alert(e?.response?.data?.message || "Tenant switch failed");
      setModeBusy(false);
    }
  };

  const onReset = async () => {
    if (!window.confirm("Reset the demo? This wipes all signals/cases/customers and reseeds. Takes ~15s for AI to re-classify.")) return;
    setResetting(true); setResetMsg("");
    try {
      await api.post("/admin/reset");
      setResetMsg("Reseeded. AI re-classifying…");
      // give Gemini a moment, then go to radar
      setTimeout(() => {
        setResetMsg("");
        setResetting(false);
        nav("/", { replace: true });
        window.location.reload();
      }, 15000);
    } catch (e) {
      setResetMsg(e?.response?.data?.detail || "Reset failed");
      setResetting(false);
    }
  };

  // Shared by the fixed sidebar and the drawer used below lg. The drawer only
  // mounts while open, and the sidebar is hidden at those widths.
  const sidebarContent = (onNavigate) => (
    <>
      <div className="px-5 py-5 flex items-center gap-2 border-b" style={{ borderColor: "var(--border)" }}>
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg,#FF453A,#BF5AF2)" }}>
          <Radar size={15} color="#fff" strokeWidth={2.4} />
        </div>
        <div>
          <div className="font-display text-[15px] leading-tight">Prioris</div>
          <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>{user.tenant_name || "tenant"}</div>
        </div>
      </div>
      <nav className="px-2 py-3 flex flex-col">
        {navSections
          .map((section) => ({
            ...section,
            items: section.items.filter((it) => !it.permission || permissions.includes(it.permission)),
          }))
          .filter((section) => section.items.length > 0)
          .map((section, i) => (
            <div key={section.label} className={i > 0 ? "mt-3.5 pt-3.5 border-t" : ""}
                 style={i > 0 ? { borderColor: "var(--border)" } : undefined}>
              <div className="px-3 mb-1 text-[10px] font-medium uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                {section.label}
              </div>
              <div className="flex flex-col gap-0.5">
                {section.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.to === "/"}
                    data-testid={it.testid}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] ${
                        isActive ? "nav-link is-active" : "nav-link"
                      }`
                    }
                    style={({ isActive }) => isActive ? { background: "var(--surface-2)" } : {}}
                  >
                    <it.icon size={15} strokeWidth={1.8} />
                    {it.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
      </nav>

      {/* Staff who also hold a membership here. Separated from the nav above
          because it leaves this workspace entirely rather than navigating
          within it. */}
      {isPlatformAdmin && (
        <div className="px-2 pt-2 mt-1 border-t" style={{ borderColor: "var(--border)" }}>
          <NavLink
            to="/admin"
            data-testid="nav-platform-console"
            onClick={onNavigate}
            className="flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] nav-link"
          >
            <ShieldAlert size={15} strokeWidth={1.8} />
            Platform console
          </NavLink>
        </div>
      )}
    </>
  );

  return (
    <div className="flex min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Sidebar: fixed from lg up, a drawer below that. */}
      <aside className="hidden lg:block w-[230px] shrink-0 border-r" style={{ borderColor: "var(--border)" }} data-testid="sidebar">
        {sidebarContent()}
      </aside>
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-[260px] max-w-[85vw] p-0 overflow-y-auto"
                      style={{ background: "var(--bg)", borderColor: "var(--border)", color: "var(--text)" }}
                      aria-describedby={undefined} data-testid="sidebar-drawer">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          {sidebarContent(() => setNavOpen(false))}
          <div className="px-5 py-4 border-t md:hidden flex" style={{ borderColor: "var(--border)" }}>
            <ThemeToggle />
          </div>
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-[56px] border-b flex items-center justify-between gap-2 px-3 md:px-6 shrink-0" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={() => setNavOpen(true)} aria-label="Open navigation"
                    data-testid="nav-open-btn" className="lg:hidden p-2 -ml-1 rounded-md hover-wash">
              <Menu size={18} />
            </button>
            <span className="hidden md:inline text-[12px] uppercase tracking-[0.16em] truncate" style={{ color: "var(--text-muted)" }}>
              Prioris · Foundation Release v0.1
            </span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 xl:gap-3 min-w-0">
            {plan && (
              <span className="hidden sm:inline px-2.5 py-1 rounded-md text-[11px] uppercase tracking-wider"
                    style={{ background: "var(--surface)", border: "1px solid var(--border)",
                             color: "var(--text-secondary)" }}
                    title={`Plan: ${plan.plan_code} (${plan.status})`}
                    data-testid="plan-pill">
                {plan.plan_code}
              </span>
            )}
            <CreditsPill credits={plan?.ai_credits} />
            {/* DEMO / LIVE mode pill — reflects the tenant environment */}
            {true && (
              <button
                onClick={pairedTenant ? onToggleMode : undefined}
                disabled={modeBusy || !pairedTenant}
                data-testid="mode-toggle-btn"
                className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-[11.5px] font-medium uppercase tracking-wider transition disabled:opacity-60 shrink-0"
                style={{
                  background: `color-mix(in srgb, ${modeColor} 12%, transparent)`,
                  border: `1px solid color-mix(in srgb, ${modeColor} 45%, transparent)`,
                  color: modeColor,
                }}
                title={pairedTenant
                  ? `Switch to ${pairedTenant.name} (${pairedTenant.environment})`
                  : `This tenant runs in ${tenantMode.toUpperCase()} mode (enforced server-side)`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: modeColor }} />
                {tenantMode === "live" ? "Live" : "Demo"}
              </button>
            )}
            {/* Demo Reset — not available in v1 */}
            {false && (
              <button
                onClick={onReset}
                disabled={resetting}
                data-testid="demo-reset-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] transition disabled:opacity-60"
                style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}
                title="Wipe operational data and reseed with fresh demo cases"
              >
                <RotateCcw size={12} className={resetting ? "animate-spin" : ""} />
                {resetting ? (resetMsg || "Resetting…") : "Demo reset"}
              </button>
            )}
            <div className="hidden md:block"><ThemeToggle /></div>
            {/* Persona switcher */}
            <div className="relative">
              <button
                onClick={() => setOpen((v) => !v)}
                data-testid="persona-switcher"
                className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-md text-[12.5px] whitespace-nowrap"
                style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
              >
                <span className="hidden xl:inline" style={{ color: "var(--text-secondary)" }}>Viewing as</span>
                <span className="font-medium">{PERSONA_LABEL[persona]}</span>
                <ChevronDown size={13} />
              </button>
              {open && (
                <div className="absolute right-0 mt-1.5 w-44 rounded-md py-1 z-50"
                     style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                  {Object.entries(PERSONA_LABEL).map(([k, v]) => (
                    <button
                      key={k}
                      data-testid={`persona-opt-${k}`}
                      onClick={() => { setPersona(k); setOpen(false); }}
                      className="w-full text-left px-3 py-2 text-[12.5px] hover-wash"
                    >
                      <div className="font-medium">{v}</div>
                      <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                        {k === "founder" ? "Business-risk lens" : k === "advisor" ? "Personal queue" : "Team-wide load"}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-[12.5px] shrink-0">
              <div className="hidden sm:flex w-7 h-7 rounded-full items-center justify-center font-mono text-[11px]"
                   style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                   title={user.name}>
                {user.name?.split(" ").map(s=>s[0]).slice(0,2).join("")}
              </div>
              <span className="hidden xl:inline whitespace-nowrap">{user.name}</span>
              <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }}
                      className="p-1.5 rounded hover-wash" title="Sign out">
                <LogOut size={14} />
              </button>
            </div>
          </div>
        </header>
        <PlanNotice plan={plan} upgrade={upgrade} onDismiss={() => setUpgrade(null)} />
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * AI credits left, in the top bar: allowance plus top-ups. Amber from 80% of
 * the allowance used, red at zero. Hidden for an unlimited allowance.
 */
function CreditsPill({ credits }) {
  if (!credits || credits.remaining == null) return null;
  const { remaining, allowance } = credits;
  const color = remaining === 0 ? "var(--risk-rev)"
    : allowance && remaining <= allowance * 0.2 ? "var(--risk-waiting)"
    : "var(--text-secondary)";
  return (
    <Link to="/settings?tab=plan" data-testid="credits-pill"
          title={`${credits.used.toLocaleString("en-IN")} of ${allowance.toLocaleString("en-IN")} AI credits used`
                 + (credits.topup_balance ? `, plus ${credits.topup_balance.toLocaleString("en-IN")} top-up` : "")}
          className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded-md text-[11px] uppercase tracking-wider whitespace-nowrap"
          style={{ background: "var(--surface)", border: "1px solid var(--border)", color }}>
      <Sparkles size={12} strokeWidth={2} aria-hidden="true" />
      {remaining.toLocaleString("en-IN")}<span className="hidden sm:inline"> AI credits</span>
    </Link>
  );
}

/**
 * Says so when the plan stops something: a 402 from any request, or AI credits
 * running low. Both point at the plan screen, where credits can be topped up.
 */
function PlanNotice({ plan, upgrade, onDismiss }) {
  const credits = plan?.ai_credits;
  const low = credits && credits.allowance
    && credits.remaining != null && credits.remaining <= credits.allowance * 0.2;

  let text = null;
  if (upgrade?.code === "ai_credits_exhausted") {
    text = "Out of AI credits. AI assessment, suggestions and reply drafts are paused until you top up.";
  } else if (upgrade) {
    text = upgrade.message || "Your plan does not include this.";
  } else if (low) {
    text = credits.remaining === 0
      ? "Out of AI credits. Top up to keep using AI."
      : `${credits.remaining.toLocaleString("en-IN")} AI credits left this period.`;
  }
  if (!text) return null;

  return (
    <div className="flex items-center gap-3 px-4 md:px-6 py-2 text-[12.5px]" role="status"
         style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}
         data-testid="plan-notice">
      <span className="flex-1">{text}</span>
      <Link to="/settings?tab=plan" className="underline" data-testid="plan-notice-link">
        {upgrade && upgrade.code !== "ai_credits_exhausted" ? "See plans" : "Buy AI credits"}
      </Link>
      {upgrade && (
        <button type="button" onClick={onDismiss} className="p-1 rounded hover-wash"
                aria-label="Dismiss">×</button>
      )}
    </div>
  );
}
